import { ActionFunctionArgs, redirect } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import {
  getAuthenticationOptions,
  authenticatePasskey,
} from '@/portal/api/Passkeys.ts';
import { ApiError } from '@/common/types/errors/ApiError.ts';
import LoginPage from '@/portal/routes/login/Page.tsx';

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get('email') as string;

  try {
    const optionsJSON = await getAuthenticationOptions({ email });

    // Authenticate using browser API
    const credential = await startAuthentication({ optionsJSON });

    // Send credential to server for verification
    await authenticatePasskey({ credential });

    return redirect('/portal');
  } catch (err) {
    console.error('Authentication failed:', err);

    let errorMessage = 'Authentication failed. Please try again.';

    // Handle API errors with user-friendly messages
    if (err instanceof ApiError) {
      switch (err.status) {
        case 404:
          errorMessage =
            'A user could not be found with this email address. Please reach out to an admin for assistance.';
          break;
        case 422:
          // This could be "No passkeys registered for this user" or other validation errors
          if (err.message.includes('passkeys')) {
            errorMessage =
              'No passkeys are registered for this account. Please contact an admin to set up your passkey access.';
          } else {
            errorMessage = err.message;
          }
          break;
        case 401:
          errorMessage =
            'Authentication failed. Your passkey may have expired or been revoked. Please contact an admin for assistance.';
          break;
        case 500:
          errorMessage =
            "We're experiencing technical difficulties. Please try again in a few minutes or contact support.";
          break;
        default:
          errorMessage =
            err.message || 'Authentication failed. Please try again.';
      }
    } else if (err instanceof Error) {
      // Handle WebAuthn browser errors with friendly messages
      if (err.name === 'NotAllowedError') {
        errorMessage =
          'Passkey authentication was cancelled or timed out. Please try again.';
      } else if (err.name === 'NotSupportedError') {
        errorMessage =
          'Passkeys are not supported on this device or browser. Please try a different device or contact support.';
      } else {
        errorMessage =
          'Authentication failed. Please try again or contact support if the problem persists.';
      }
    }

    return {
      success: false,
      flash: {
        message: errorMessage,
        type: 'danger' as const,
      },
    };
  }
}

export default function LoginRoute() {
  return <LoginPage />;
}
